/**
 * The marketing AI calls.
 *
 * Like the rest of `services/ai.js`, these go through the server so the model
 * key never reaches the browser. The server's `/ai/chat` already attaches the
 * client book and pipeline as context, so the prompts here only need to supply
 * the marketing-specific part — the audience, the channel, the goal.
 */
import api from '../lib/api.js';
import { AIUnavailable } from './ai.js';
import { isOffline } from '../lib/storage.js';
import { MERGE } from '../constants/marketing.js';

function assertReady(S) {
  if (S && S.aiOn === false) throw new AIUnavailable('AI features are switched off in Settings → AI');
  if (isOffline()) throw new AIUnavailable('Sign in to use AI — the model runs on the server');
}

const explain = e => {
  if (e instanceof AIUnavailable) return e;
  if (e && e.status === 429) return new AIUnavailable('Too many AI requests — give it a minute');
  return new AIUnavailable((e && e.message) || 'The AI request failed');
};

/** One-shot prompt through the chat endpoint. */
async function ask(S, content) {
  assertReady(S);
  try {
    return await api.ai.chat([{ role: 'user', content }]).then(r => r.reply);
  } catch (e) {
    throw explain(e);
  }
}

/** Pull the first JSON object or array out of a reply that may be fenced. */
export function parseModelJSON(text) {
  const clean = String(text).replace(/```json/gi, '').replace(/```/g, '').trim();
  const arrayAt = clean.indexOf('[');
  const objectAt = clean.indexOf('{');
  const start = (arrayAt > -1 && (objectAt < 0 || arrayAt < objectAt)) ? arrayAt : objectAt;
  if (start < 0) throw new AIUnavailable('The model did not return JSON');
  const end = start === arrayAt ? clean.lastIndexOf(']') : clean.lastIndexOf('}');
  try {
    return JSON.parse(clean.slice(start, end + 1));
  } catch {
    throw new AIUnavailable('The model returned JSON that could not be read');
  }
}

const mergeList = MERGE.map(m => m[0]).join(' ');

const senderLine = S =>
  `Sender: ${S.senderName || 'the team'} at ${S.companyName || ''}. Tone: ${S.tone || 'Friendly and direct'}.`;

/** Three campaigns worth running, based on what the book actually shows. */
export const campaignIdeas = (S, audiences) => ask(S,
  `Existing audiences: ${JSON.stringify(audiences)}\n\n`
  + 'Suggest the three marketing campaigns worth running this month. For each: who to target '
  + 'and how many, which channel and why that channel, the angle, and the revenue it could '
  + `protect or win in ${S.currency || 'AED'}. Base it only on what the data shows. Under 220 words.`);

/** Write the copy for one campaign. Returns { subject, body }. */
export async function writeCampaign(S, { audience, size, sample, channel, goal }) {
  const guidance = channel === 'email'
    ? 'Email: under 130 words, one clear ask, a subject line under 60 characters.'
    : channel === 'sms'
      ? 'SMS: under 155 characters total including the merge fields. Set subject to "".'
      : channel === 'push'
        ? 'Push: title under 40 characters as the subject, body under 120 characters.'
        : 'WhatsApp: under 55 words, no subject, no formal sign-off. Set subject to "".';

  const reply = await ask(S,
    `Audience: ${audience} (${size} people)\nSample records: ${JSON.stringify(sample)}\n`
    + `Channel: ${channel}\nGoal: ${goal}\n${senderLine(S)}\n\n`
    + `Write the campaign message. Use these merge fields where they help, exactly as written: ${mergeList}. `
    + `${guidance} Reply ONLY with JSON: {"subject":"...","body":"..."}`);

  return parseModelJSON(reply);
}

/** Design a multi-step journey. Returns { name, audienceId, why, steps }. */
export async function buildSequence(S, { audiences, aim, goal }) {
  const reply = await ask(S,
    `Audiences available: ${JSON.stringify(audiences)}\n`
    + (aim ? `Aim at: ${JSON.stringify(aim)}\n` : '')
    + `Goal: ${goal || 'improve revenue from the most neglected group'}\n${senderLine(S)}\n\n`
    + `Design a multi-step journey. Use merge fields exactly as written where useful: ${mergeList}. `
    + 'Mix channels sensibly — email for detail, WhatsApp for nudges. Include waits and an exit condition. '
    + 'Reply ONLY with JSON: {"name":"...","audienceId":"...","why":"one sentence",'
    + '"steps":[{"type":"message|wait|task|notify|exit","channel":"email|whatsapp|sms|push","days":3,'
    + '"subject":"","body":"","title":"","when":"paid|replied|converted|optout"}]}');

  return parseModelJSON(reply);
}

/** Work out the filters for an audience described in plain English. */
export async function findAudience(S, { description, flags, plans, statuses, types }) {
  const reply = await ask(S,
    `Available values: ${JSON.stringify({ flags, plans, statuses, types })}\n\n`
    + `Request: "${description}"\n\n`
    + 'Reply ONLY with JSON: {"name":"a short name for this segment","src":"clients|contacts",'
    + '"flag":"","status":"","plan":"","type":"","owner":"","why":"one sentence"}. '
    + 'Use exact values from the lists, empty string when not implied.');

  return parseModelJSON(reply);
}

/** Free-text studio tools that return prose rather than JSON. */
export function runStudioTool(S, tool, { input, audience, mode, channel }) {
  const audienceLine = audience ? `Audience: ${JSON.stringify(audience)}\n` : '';

  if (tool === 'subjects') {
    return ask(S, `${audienceLine}Campaign: ${input}\n\n`
      + 'Give five subject line variants, each under 60 characters, with one line on why each might win. '
      + 'Number them.');
  }
  if (tool === 'rewrite') {
    return ask(S, `Message:\n${input}\n\nChannel: ${channel}. ${senderLine(S)}\n\n`
      + `Rewrite it to be ${mode}. Keep any {{merge}} fields exactly as written. `
      + 'Give the rewritten message only, no commentary.');
  }
  if (tool === 'grade') {
    return ask(S, `Message:\n${input}\n\n`
      + 'Grade it: length, clarity, spam triggers, and how likely it is to get a reply. '
      + 'Give a predicted reply rate as a range, then the three fixes that would help most. Under 180 words.');
  }
  if (tool === 'reply') {
    return ask(S, `They wrote back:\n${input}\n\n${senderLine(S)}\n\n`
      + 'Classify the intent in one line — interested, objection, not now, wrong person, or unsubscribe — '
      + 'then draft my response. Under 140 words.');
  }
  return ask(S, input);
}
